import { useRef, useState } from 'react'
import { Button } from '@sarkiai/ui'
import { AnimatedTextCompletion } from '@/components/ui/animated-text-completion'
import { Bot, Copy, ThumbsUp, ThumbsDown, Check, ChevronDown, RotateCw } from "lucide-react"
import { useTranslations } from '@/components/I18nProvider'
import { UIMessage } from '@ai-sdk/react'
import { extractMessageContent } from '@sarkiai/utils/ai-compat'
import StarBorder from '@/components/ui/StarBorder'
import ShinyText from '@/components/ui/ShinyText'
import { motion } from 'framer-motion'

interface ChatMessageProps {
  message: UIMessage
  isLast: boolean
  status: string
  onRegenerate?: () => void
}

export function ChatMessage({ message, isLast, status, onRegenerate }: ChatMessageProps) {
  const t = useTranslations('chat')
  const isUser = message.role === 'user'
  const isActive = (status === 'submitted' || status === 'streaming') && isLast
  const isStreaming = status === 'streaming' && isLast
  const content = extractMessageContent(message)

  // Extract reasoning if available
  const reasoning = (message as any).reasoning || (message.parts?.find((p: any) => p.type === 'reasoning') as any)?.reasoning
  const isReasoningStreaming = isStreaming && !!reasoning && !content

  const [copied, setCopied] = useState(false)
  const [rating, setRating] = useState<'positive' | 'negative' | null>(null)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  if (isUser) {
    return (
      <div className="flex flex-col gap-2 sm:gap-3 py-4 mt-2 items-end mx-1">
        <div className="flex items-start gap-2 sm:gap-3 max-w-[90%] sm:max-w-[85%] flex-row-reverse">
          <div className="flex flex-col gap-1.5 sm:gap-2 flex-1 min-w-0">
            <StarBorder
              as="div"
              className="rounded-2xl rounded-tr-none"
              color="hsl(var(--primary))"
              speed="3s"
              thickness={2}
              isAnimate={false}
            >
              <div className="px-4 py-2 rounded-2xl rounded-tr-none text-xs sm:text-sm leading-relaxed whitespace-pre-wrap bg-primary text-primary-foreground font-semibold shadow-lg">
                {content}
              </div>
            </StarBorder>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2 sm:gap-2 py-4 items-start mx-2">
      <div className="flex items-start gap-2 sm:gap-3 w-full">
        {reasoning && (
          <div className="flex-1 min-w-0 mt-2 ml-10">
            <details className="group" open={isReasoningStreaming}>
              <summary className="flex items-center gap-2 text-[10px] font-bold text-muted-foreground/60 cursor-pointer list-none hover:text-muted-foreground/80 transition-colors tracking-[0.2em] outline-none">
                <ShinyText
                  text={t('reasoning') || "RÉFLEXION"}
                  speed={2}
                  delay={0}
                  color="hsl(var(--muted-foreground))"
                  shineColor="hsl(var(--foreground))"
                  spread={120}
                  direction="left"
                  yoyo={false}
                  pauseOnHover={false}
                  disabled={!isActive}
                />
                <ChevronDown className="w-3 h-3 group-open:rotate-180 transition-transform text-muted-foreground/30" />
              </summary>
              <div className="mt-2 pl-3 border-l-2 border-primary/20 text-[11px] text-muted-foreground italic leading-relaxed font-medium">
                <AnimatedTextCompletion isLast={isLast} text={reasoning} isStreaming={isActive} />
              </div>
            </details>
          </div>
        )}
      </div>

      <div className="flex items-start gap-2 sm:gap-3 w-full">
        {/* <div className="relative shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center border border-primary/20 shadow-sm mt-1">
          <Bot className="h-4 w-4 text-primary" aria-hidden="true" />
        </div> */}

        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <div className="bg-transparent text-xs sm:text-sm font-medium text-foreground">
            {content ? (
              <AnimatedTextCompletion
                isLast={isLast}
                text={content}
                isStreaming={isActive}
              />
            ) : isStreaming && !reasoning ? (
              <div className="h-4 w-4 rounded-full bg-primary/20 animate-pulse" />
            ) : null}

            {isStreaming && (
              <div className="flex items-center gap-3 mt-3">
                <motion.div
                  animate={{
                    scale: [1, 1.15, 1.15, 1, 1],
                    backgroundColor: ["hsl(var(--primary))", "#ffffff", "hsl(var(--primary))", "#ffffff", "hsl(var(--primary))"]
                  }}
                  transition={{ duration: 2, ease: "easeInOut", repeat: Infinity }}
                  className="w-3 h-3 rounded-full"
                />
              </div>
            )}

            {!isActive && content && (
              <div className="flex items-center gap-1 mt-3 pt-2 border-t border-border/40">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs gap-1.5 hover:bg-muted"
                  onClick={handleCopy}
                  disabled={copied}
                >
                  {copied ? (
                    <Check className="h-3.5 w-3.5 text-green-500" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </Button>

                <div className="flex items-center gap-0.5 ml-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className={`h-7 px-2 hover:bg-muted ${rating === 'positive' ? 'text-green-600' : ''}`}
                    onClick={() => setRating('positive')}
                  >
                    <ThumbsUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className={`h-7 px-2 hover:bg-muted ${rating === 'negative' ? 'text-red-600' : ''}`}
                    onClick={() => setRating('negative')}
                  >
                    <ThumbsDown className="h-3.5 w-3.5" />
                  </Button>
                  {onRegenerate && isLast && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs gap-1.5 hover:bg-muted ml-auto"
                      onClick={onRegenerate}
                    >
                      <RotateCw className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
